// src/pages/Reports.js
import React, { useState, useEffect } from 'react';
import { Container, Card, Row, Col, Alert, Spinner } from 'react-bootstrap';
import useWebsiteStore from '../store/websiteStore';
import { useToastStore } from '../components/UI/Toast';
import { reportService } from '../api/services/reportService';

import ReportControls from '../components/Reports/ReportControls';
import WebsiteSelector from '../components/Reports/WebsiteSelector';
import ReportChart from '../components/Reports/ReportChart';
import ReportSummary from '../components/Reports/ReportSummary';

const Reports = () => {
    const [reportType, setReportType] = useState('uptime');
    const [dateRange, setDateRange] = useState('30days');
    const [selectedWebsites, setSelectedWebsites] = useState([]);
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isExporting, setIsExporting] = useState(false);

    const addToast = useToastStore(state => state.addToast);
    const { websites, fetchWebsites, loading: websitesLoading, error: websitesError } = useWebsiteStore();

    // Načtení webů, pokud ještě nejsou načteny
    useEffect(() => {
        if (websites.length === 0 && !websitesLoading) {
            fetchWebsites();
        }
    }, []);

    // Výchozí výběr všech webů, když se načtou
    useEffect(() => {
        if (websites.length > 0 && selectedWebsites.length === 0) {
            setSelectedWebsites(websites.map(w => w.id));
        }
    }, [websites, selectedWebsites.length]);

    // Načtení dat reportu
    useEffect(() => {
        if (selectedWebsites.length === 0) return;
        fetchReportData();
    }, [reportType, dateRange, selectedWebsites, websites]);

    const onTimeRangeChange = (timeRange) => {
        setDateRange(timeRange);
    }

    const fetchReportData = async () => {
        if (loading) {
            return;
        }
        setLoading(true);
        setError(null);

        try {
            const data = await reportService.fetchData(reportType, dateRange, selectedWebsites, websites);
            setReportData(data);
        } catch (err) {
            console.error('Error fetching report data:', err);
            setError(err.message || 'Nastala chyba při načítání dat');
        } finally {
            setLoading(false);
        }
    };

    const handleWebsiteToggle = (websiteId) => {
        setSelectedWebsites(prev =>
            prev.includes(websiteId) ?
                prev.filter(id => id !== websiteId) :
                [...prev, websiteId]
        );
    };

    const handleSelectAll = () => setSelectedWebsites(websites.map(w => w.id));
    const handleSelectNone = () => setSelectedWebsites([]);

    const handleExport = (type) => {
        if (!reportData || !reportData.datasets || reportData.datasets.length === 0) {
            addToast('Nejsou k dispozici žádná data pro export', 'warning');
            return;
        }

        setIsExporting(true);

        try {
            if (type === 'csv') {
                reportService.exportToCsvFile(reportData, reportType, dateRange);
                addToast('Report byl úspěšně exportován jako CSV', 'success');
            } else if (type === 'pdf') {
                addToast('Export do PDF bude brzy k dispozici', 'info');
            } else if (type === 'email') {
                addToast('Funkce odeslání emailem bude brzy k dispozici', 'info');
            }
        } catch (error) {
            addToast(`Chyba při exportu: ${error.message}`, 'danger');
        } finally {
            setIsExporting(false);
        }
    };

    const refetch = () => fetchReportData();

    if (websitesLoading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" role="status" variant="primary">
                    <span className="visually-hidden">Načítání webů...</span>
                </Spinner>
            </div>
        );
    }

    if (websitesError) {
        return <Alert variant="danger">{websitesError}</Alert>;
    }

    return (
        <Container fluid className="">
            <h1 className="mb-4 ">Reporty</h1>

            <ReportControls
                reportType={reportType}
                setReportType={setReportType}
                dateRange={dateRange}
                setDateRange={setDateRange}
            />

            <Row>
                <Col lg={3} className="mb-4">
                    <WebsiteSelector
                        websites={websites}
                        selectedWebsites={selectedWebsites}
                        onWebsiteToggle={handleWebsiteToggle}
                        onSelectAll={handleSelectAll}
                        onSelectNone={handleSelectNone}
                    />
                </Col>

                <Col lg={9}>
                    <ReportChart
                        reportType={reportType}
                        reportData={reportData}
                        loading={loading}
                        dateRange={dateRange}
                        error={error}
                        selectedWebsites={selectedWebsites}
                        isExporting={isExporting}
                        onExport={handleExport}
                        onRefresh={refetch}
                        onTimeRangeChange={onTimeRangeChange}
                    />

                    {!loading && reportData?.summary && (
                        <ReportSummary
                            reportType={reportType}
                            summary={reportData.summary}
                        />
                    )}
                </Col>
            </Row>
        </Container>
    );
};

export default Reports;